/**
 * Admin Content Authoring page — top-level shell.
 *
 * Route: /admin/authoring · Requires admin JWT (same token used by /admin).
 *
 * File layout (post refactor, Feb 2026 · Phase 2G):
 *   ├── AdminAuthoring.jsx          ← this file: page chrome, module rail, top-right actions
 *   └── authoring/
 *       ├── coverage.jsx            ← heatClass/heatColor helpers + CoverageSummary + SummaryChip
 *       ├── DraftCard.jsx           ← shared AI-draft candidate card
 *       ├── ModuleEditor.jsx        ← right-pane tab switcher (Lessons / Questions / Metadata)
 *       ├── LessonsTab.jsx          ← lessons list + LessonEditor + LessonSuggesterPanel
 *       ├── QuestionsTab.jsx        ← questions list, per-question editor, gap + LO-drafter panels
 *       ├── MetadataTab.jsx         ← module metadata form
 *       └── BankPanels.jsx          ← ImportBankPanel · ExportBankPanel · CourseGapPanel
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowLeft, ChevronRight, Download, FileText, GitCompare, Headphones, Home, Image as ImageIcon, ListChecks,
  LogOut, RefreshCw, Share2, Upload, UserPlus,
} from "lucide-react";
import { adminApi, getAdminToken } from "./portal/shell";
import "./viewer/viewer.css";
import "./portal/portal.css";
import { CoverageSummary, heatClass, heatColor } from "./authoring/coverage";
import { ModuleEditor } from "./authoring/ModuleEditor";
import {
  ImportBankPanel, ExportBankPanel, RoundTripCheckPanel, CourseGapPanel,
  MissingDiagramsPanel, ContentSyncPanel, BuyerSimPanel, AccountingPanel,
  TtsWarmupPanel,
} from "./authoring/BankPanels";

const COURSE_ID = "advanced-rpas-2026";
const TOKEN_KEY = "tbg_admin_token";
const EXP_KEY = "tbg_admin_token_exp";

const clearAdminToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXP_KEY);
};

export default function AdminAuthoring() {
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [roundTripping, setRoundTripping] = useState(false);
  const [courseGapOpen, setCourseGapOpen] = useState(false);
  const [missingDiagramsOpen, setMissingDiagramsOpen] = useState(false);
  const [contentSyncOpen, setContentSyncOpen] = useState(false);
  const [buyerSimOpen, setBuyerSimOpen] = useState(false);
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [ttsWarmupOpen, setTtsWarmupOpen] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin", { replace: true }); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    setBusy(true);
    try {
      const c = await adminApi.courseAuthoring(COURSE_ID);
      setCourse(c);
      if (!selectedModuleId && c.modules?.length) {
        setSelectedModuleId(c.modules[0].id);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        clearAdminToken(); nav("/admin", { replace: true }); return;
      }
      toast.error(e.message || "Load failed");
    } finally { setBusy(false); }
  };

  const signOut = () => { clearAdminToken(); nav("/admin", { replace: true }); };

  const selectedModule = useMemo(() => {
    if (!course || !selectedModuleId) return null;
    return course.modules.find((m) => m.id === selectedModuleId) || null;
  }, [course, selectedModuleId]);

  const counts = course?._question_counts || {};

  return (
    <div className="tbg-vw scroll" data-testid="admin-authoring">
      <Toaster theme="dark" position="top-right" />
      <div className="tbg-vw-top">
        <Link to="/admin" className="tbg-vw-brand">
          <img src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/3gk1w13g_FullLogo_Transparent_NoBuffer%20%2812%29.png" alt="TBG" />
          <span className="sub">Content Authoring</span>
        </Link>
        <div className="tbg-vw-mode">
          <span style={{ font: "600 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
            color: "#7b8b87", textTransform: "uppercase" }}>
            {course?.title || "Loading…"}
          </span>
          {course?._marked_for_reassign_total > 0 && (
            <span data-testid="authoring-course-marked-total" style={{
              marginLeft: 12, padding: "3px 10px",
              color: "#c9a8ff", background: "rgba(156,108,248,0.08)",
              border: "1px solid rgba(156,108,248,0.5)", borderRadius: 3,
              font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
            }} title="Questions flagged for reassignment across the whole course. Persists server-side.">
              {course._marked_for_reassign_total} MARKED
            </span>
          )}
        </div>
        <div className="tbg-vw-actions">
          <button className="tbg-vw-btn" onClick={() => setImporting(true)}
            data-testid="authoring-import-open"
            style={{ borderColor: "rgba(109,184,255,0.6)", color: "#6db8ff" }}
            title="Bulk-import a question bank JSON/CSV into modules by slug">
            <Upload size={12} /> Import bank
          </button>
          <button className="tbg-vw-btn" onClick={() => setExporting(true)}
            data-testid="authoring-export-open"
            style={{ borderColor: "rgba(61,192,175,0.6)", color: "#3dc0af" }}
            title="Download a full snapshot of the bank (all states) for backup / versioning">
            <Download size={12} /> Export bank
          </button>
          <button className="tbg-vw-btn" onClick={() => setRoundTripping(true)}
            data-testid="authoring-roundtrip-open"
            style={{ borderColor: "rgba(240,190,60,0.6)", color: "#f0be3c" }}
            title="Compare your local reference file against the current bank to see what drifted">
            <GitCompare size={12} /> Round-trip check
          </button>
          <button className="tbg-vw-btn" onClick={() => setCourseGapOpen(true)}
            data-testid="authoring-course-gap-open"
            style={{ borderColor: "rgba(156,108,248,0.6)", color: "#c9a8ff" }}
            title="Run LO coverage gap analysis across every module at once">
            <ListChecks size={12} /> Analyze all modules
          </button>
          <button className="tbg-vw-btn" onClick={() => setMissingDiagramsOpen(true)}
            data-testid="authoring-missing-diagrams-open"
            style={{ borderColor: "rgba(239,106,106,0.5)", color: "#ef6a6a" }}
            title="Scan lesson bodies for ![…](asset:slug) tokens that don't resolve, plus orphaned uploads">
            <ImageIcon size={12} /> Check diagrams
          </button>
          <button className="tbg-vw-btn" onClick={() => setContentSyncOpen(true)}
            data-testid="authoring-content-sync-open"
            style={{ borderColor: "rgba(201,168,255,0.6)", color: "#c9a8ff" }}
            title="Export/import content bundles between Preview and Production (questions, assets, courses)">
            <Share2 size={12} /> Content sync
          </button>
          <button className="tbg-vw-btn" onClick={() => setBuyerSimOpen(true)}
            data-testid="authoring-buyer-sim-open"
            style={{ borderColor: "rgba(61,192,175,0.6)", color: "#3dc0af" }}
            title="Simulate a buyer purchase (no Stripe) or resend a magic link for an existing student">
            <UserPlus size={12} /> Buyer sim
          </button>
          <button className="tbg-vw-btn" onClick={() => setAccountingOpen(true)}
            data-testid="authoring-accounting-open"
            style={{ borderColor: "rgba(240,190,60,0.6)", color: "#f0be3c" }}
            title="Download the CRA tax remittance report (transactions, per-jurisdiction summary, refunds) as a ZIP for your bookkeeper">
            <FileText size={12} /> Accounting
          </button>
          <button className="tbg-vw-btn" onClick={() => setTtsWarmupOpen(true)}
            data-testid="authoring-tts-warmup-open"
            style={{ borderColor: "rgba(61,192,175,0.6)", color: "#3dc0af" }}
            title="Pre-generate natural-voice narration for every lesson so the first listener gets instant playback">
            <Headphones size={12} /> Narration
          </button>
          <button className="tbg-vw-btn ghost" onClick={reload} title="Refresh">
            <RefreshCw size={12} className={busy ? "spin" : ""} /> Refresh
          </button>
          <Link to="/admin" className="tbg-vw-btn ghost"><ArrowLeft size={12} /> Admin</Link>
          <Link to="/" className="tbg-vw-btn ghost"><Home size={12} /> Site</Link>
          <button className="tbg-vw-btn ghost" onClick={signOut} data-testid="authoring-logout">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>

      {importing && (
        <ImportBankPanel
          courseId={COURSE_ID}
          course={course}
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); reload(); }}
        />
      )}
      {exporting && (
        <ExportBankPanel
          courseId={COURSE_ID}
          course={course}
          onClose={() => setExporting(false)}
        />
      )}
      {roundTripping && (
        <RoundTripCheckPanel
          courseId={COURSE_ID}
          onClose={() => setRoundTripping(false)}
        />
      )}
      {courseGapOpen && (
        <CourseGapPanel courseId={COURSE_ID} onClose={() => setCourseGapOpen(false)} />
      )}
      {missingDiagramsOpen && (
        <MissingDiagramsPanel courseId={COURSE_ID} onClose={() => setMissingDiagramsOpen(false)} />
      )}
      {contentSyncOpen && (
        <ContentSyncPanel onClose={() => setContentSyncOpen(false)} />
      )}
      {buyerSimOpen && (
        <BuyerSimPanel onClose={() => setBuyerSimOpen(false)} />
      )}
      {accountingOpen && (
        <AccountingPanel onClose={() => setAccountingOpen(false)} />
      )}
      {ttsWarmupOpen && (
        <TtsWarmupPanel courseId={COURSE_ID} onClose={() => setTtsWarmupOpen(false)} />
      )}

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16,
        padding: 16, background: "#050807", minHeight: "calc(100vh - 56px)" }}>
        {/* Left rail — module list */}
        <div>
          <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
            color: "#7b8b87", textTransform: "uppercase", padding: "4px 8px 12px" }}>
            Modules
          </div>
          {course?.modules?.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((m) => {
            const c = counts[m.id] || {};
            const target = c.bank_target || 0;
            const real = c.real_exam_questions || 0;
            const pct = c.coverage_pct || 0;
            const authorPending = c.author_pending || 0;
            return (
              <div
                key={m.id}
                className={`tbg-auth-modrow${selectedModuleId === m.id ? " active" : ""}`}
                onClick={() => setSelectedModuleId(m.id)}
                data-testid={`authoring-module-${m.code}`}
              >
                <div className="code">{m.code}</div>
                <div className="title">
                  {m.title}
                  {m.is_bonus && <span style={{ marginLeft: 6, color: "#f0be3c", fontSize: 10, letterSpacing: "0.14em" }}>· BONUS</span>}
                  {!m.is_bonus && target > 0 && (
                    <div className="tbg-heat-bar" style={{ marginTop: 6 }} title={`${real}/${target} real questions · ${pct}% coverage`}>
                      <span className={heatClass(pct)} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  )}
                  {authorPending > 0 && (
                    <div style={{ marginTop: 4, color: "#6db8ff", font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.14em" }}
                      data-testid={`authoring-module-authpending-${m.code}`}>
                      +{authorPending} AUTHOR PENDING
                    </div>
                  )}
                  {(c.marked_for_reassign || 0) > 0 && (
                    <div style={{ marginTop: 4, color: "#c9a8ff", font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.14em" }}
                      data-testid={`authoring-module-marked-${m.code}`}>
                      {c.marked_for_reassign} MARKED
                    </div>
                  )}
                </div>
                <div className="counts">
                  {m.is_bonus ? "—" : (
                    <>
                      <div style={{ color: heatColor(pct) }} data-testid={`authoring-coverage-${m.code}`}>
                        {real}/{target}
                      </div>
                      <div style={{ color: "#7b8b87", fontSize: 9, marginTop: 2 }}>L{m.lessons?.length || 0}</div>
                    </>
                  )}
                </div>
                <ChevronRight size={12} color="#3dc0af" />
              </div>
            );
          })}
          <CoverageSummary course={course} />
        </div>

        {/* Right pane — module editor */}
        <div style={{ background: "#080e0d", border: "1px solid #1c2a27", borderRadius: 6, padding: 20 }}>
          {selectedModule ? (
            <ModuleEditor
              key={selectedModule.id}
              module={selectedModule}
              counts={counts[selectedModule.id]}
              courseModules={course?.modules || []}
              onChange={reload}
            />
          ) : (
            <div style={{ color: "#7b8b87" }}>Select a module to edit.</div>
          )}
        </div>
      </div>
    </div>
  );
}
